import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

/**
 * Bloc éditorial réutilisable pour les gestes de soutien.
 * Utilisé pour Sponsoring, Mécénat, Adhésion, Bénévolat numérique.
 * Même grammaire visuelle que AdoptionSection (chaleureuse, non transactionnelle).
 */
export default function SupportLanding({
  id,
  tag,
  title,           // ex: "Sponsoriser."
  quote,           // citation italique
  intro,           // paragraphe droit
  micro,           // 2ème petit paragraphe
  promises,        // [{ Icon, title, body }]
  ctaLabel,        // ex: "Recevoir le dossier sponsoring"
  ctaMicro,        // ex: "3 min · redirection HelloAsso"
  onOpen,
  reverse = false, // inverser l'orientation du hero (accent visuel alterné)
}) {
  const { lang } = useLang();
  const en = lang === "en";
  return (
    <section
      id={id}
      className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1500px] mx-auto scroll-mt-32"
      data-testid={`landing-${id}`}
    >
      {/* Header */}
      <div className={`grid md:grid-cols-12 gap-10 ${reverse ? "md:flex-row-reverse" : ""}`}>
        <div className="md:col-span-7">
          <div className="meta-label text-cinabre">{tag}</div>
          <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
            {title}
          </h2>
          {quote && (
            <p className="mt-8 font-serif text-xl md:text-2xl italic text-fg/85 leading-[1.4] max-w-2xl">
              « {quote} »
            </p>
          )}
        </div>
        <div className="md:col-span-5 self-end">
          {intro && (
            <p className="text-fg/80 leading-relaxed">{intro}</p>
          )}
          {micro && (
            <p className="mt-4 text-fg/70 text-sm leading-relaxed">{micro}</p>
          )}
        </div>
      </div>

      {/* Promesses */}
      {promises && promises.length > 0 && (
        <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-8">
          {promises.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="border border-fg/15 p-8 md:p-10 hover:border-cinabre transition-colors"
              data-testid={`${id}-promise-${i}`}
            >
              <p.Icon size={22} strokeWidth={1.3} className="text-cinabre" />
              <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight mt-6">{p.title}</h3>
              <p className="mt-4 text-fg/70 leading-relaxed text-[15px]">{p.body}</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* CTA */}
      <div className="mt-14 md:mt-20 border-t border-fg/15 pt-12 md:pt-16">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="meta-label text-fg/50">§ {en ? "Next step" : "Prochaine étape"}</div>
            <h3 className="mt-4 font-serif font-light text-3xl md:text-5xl leading-[1] tracking-tight">
              {en ? <>Fill in your <em className="italic">details.</em></> : <>Renseignez vos <em className="italic">coordonnées.</em></>}
            </h3>
            <p className="mt-5 text-fg/70 leading-relaxed max-w-2xl">
              {en ? "A short form, 3 minutes to fill in. We will get back to you personally — no automated platform, no spam." : "Une fiche courte, à remplir en 3 minutes. Nous vous reviendrons personnellement — pas de plateforme automatique, pas de spam."}
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <button
              type="button"
              onClick={onOpen}
              className="inline-flex items-center gap-3 bg-cinabre text-bg px-8 py-5 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-fg transition-colors group"
              data-testid={`${id}-open-form`}
            >
              {ctaLabel}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
            {ctaMicro && (
              <div className="mt-4 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
                {ctaMicro}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
